import React, { Component } from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';
import './ReactDatePickerWrapper.css';
import BzmTimeRangePicker, {
  TimeRange,
} from 'views/Analytics/AnalyticsMockService/Reporting/Pickers/BzmDateRangePicker/BzmTimeRangePicker';
import _ from 'lodash';
import { formatDate, getHoursFromDate, getModeFromDate, getTimeStringFromDate } from 'utils/dateUtil';

/**
 * Simple wrapper for React Day Picker component.
 */
type ReactDatePickerWrapperProps = {
  showTimePicker?: boolean;

  /** selected date range - from */
  from: any;

  /** selected date range - to*/
  to: any;

  /** Array of days to be highlighted in the table */
  highlightedDays?: any[];

  /** Earliest date that can be selected in the calendar. Days before it are locked. */
  minSelectableDate?: Date;

  /**
   * Triggered when date range changes.
   * @param from
   * @param to
   */
  onRangeChanged: (from: any, to: any) => void;
};

class ReactDatePickerWrapper extends Component<ReactDatePickerWrapperProps> {
  state = {
    timeFrom: {
      hours: getHoursFromDate(this.props.from),
      minutes: (this.props.from as Date).getMinutes(),
      mode: getModeFromDate(this.props.from),
    },
    timeTo: {
      hours: getHoursFromDate(this.props.to),
      minutes: (this.props.to as Date).getMinutes(),
      mode: getModeFromDate(this.props.to),
    },
    from: this.props.from,
    to: this.props.to,
    enteredTo: null, // Keep track of the last day for mouseEnter.

    currentTimeFrom: getTimeStringFromDate(this.props.from),
    currentTimeTo: getTimeStringFromDate(this.props.to),
  };

  formatHours = (timeRange: TimeRange) => {
    if (timeRange.mode === 'AM' && timeRange.hours === 12) {
      return 0;
    }
    if (timeRange.mode === 'PM' && timeRange.hours !== 12) {
      return timeRange.hours + 12;
    }
    return timeRange.hours;
  };

  timeRangeChanged = (timeFrom: TimeRange, timeTo: TimeRange) => {
    const fromAndTime: any = _.cloneDeep(this.state.from);
    const toAndTime: any = _.cloneDeep(this.state.to);
    if (fromAndTime != null) {
      (fromAndTime as Date).setHours(this.formatHours(timeFrom), timeFrom.minutes);
    }
    if (toAndTime != null) {
      (toAndTime as Date).setHours(this.formatHours(timeTo), timeTo.minutes);
    }
    this.props.onRangeChanged(fromAndTime, toAndTime);
  };

  formatTimeFromTimeRange = (timeRange: TimeRange) => {
    return timeRange.hours + ':' + _.padStart(timeRange.minutes.toString(), 2, '0') + ' ' + timeRange.mode;
  };

  getTimeRange = (timeFrom: TimeRange, timeTo: TimeRange) => {
    this.setState({
      currentTimeFrom: this.formatTimeFromTimeRange(timeFrom),
      currentTimeTo: this.formatTimeFromTimeRange(timeTo),
    });
  };

  componentDidUpdate(prevProps: Readonly<ReactDatePickerWrapperProps>) {
    if (prevProps.from !== this.props.from || prevProps.to !== this.props.to) {
      this.setState({ from: this.props.from, to: this.props.to });
    }
  }

  isSelectingFirstDay(from: any, to: any, day: any) {
    const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
    const isRangeSelected = from && to;
    return !from || isBeforeFirstDay || isRangeSelected;
  }

  // Deprecated
  selectDayHandler = (day: any) => {
    this.setState({ from: day });
  };

  handleResetClick = () => {
    this.setState({
      from: null,
      to: null,
      enteredTo: null,
    });
  };

  handleDayClick = async (day: any) => {
    const { from, to }: any = this.state;

    if (from && to && day >= from && day <= to) {
      await this.handleResetClick();
      return;
    }
    if (this.isSelectingFirstDay(from, to, day)) {
      await this.setState({
        from: day,
        to: null,
        enteredTo: null,
      });
    } else {
      await this.setState({
        to: day,
        enteredTo: day,
      });

      if (this.props.showTimePicker === undefined || this.props.showTimePicker === false) {
        // Propagate change of the range after some time so user
        // sees his selected range for small fraction of time
        setTimeout(() => {
          this.props.onRangeChanged(this.state.from, this.state.to);
        }, 300);
      }
    }
  };

  mouseEnterDayHandler = (day: any) => {
    const { from, to } = this.state;
    if (!this.isSelectingFirstDay(from, to, day)) {
      this.setState({
        enteredTo: day,
      });
    }
  };

  /**
   * Locked days cannot be selected in the calendar. It should be all days from today on (future cannot be selected)
   * together with days before start of the interval if user started new selection.
   */
  getLockedDays = () => {
    // Always lock after today
    const lockModifier: { after: Date; before?: Date } = { after: new Date() };

    // Lock everything before the earliest selectable date, if one was provided
    if (this.props.minSelectableDate) {
      lockModifier.before = this.props.minSelectableDate;
    }

    // Once the first day of a new selection is picked, the second click must fall
    // between that day and today — overrides any minSelectableDate lock above.
    if (this.state.enteredTo !== null) {
      return { ...lockModifier, before: this.state.from };
    }

    return lockModifier;
  };

  render() {
    // Set initial month to previous month to make it more user friendly
    const initialMonth = new Date();
    initialMonth.setDate(1);
    initialMonth.setMonth(initialMonth.getMonth() - 1);

    const { from, to, enteredTo }: any = this.state;
    const selectedDays: any = [from, { from: from, to: enteredTo ? enteredTo : to }];
    const modifiers: any = { start: from, end: to };
    const disabledDays = this.getLockedDays();

    const validateRange: boolean =
      from !== null && to !== null && from.toLocaleDateString() === to.toLocaleDateString();

    const CalendarHeader = () => {
      return (
        <div className="calendar-header text-center">
          {!from && !to && 'Please select the first day.'}
          {from && !to && 'Please select the last day.'}
          {from && to && (
            <span>
              {formatDate(from)} {this.props.showTimePicker && this.state.currentTimeFrom}
              <span className={`ml-1 mr-1`}> - </span>
              {formatDate(to)} {this.props.showTimePicker && this.state.currentTimeTo}
            </span>
          )}
        </div>
      );
    };

    return (
      <div className="BzmDatePicker position-relative">
        <CalendarHeader />
        <div className="calendar-body">
          <div className="divider"></div>
          <DayPicker
            className="bzm-date-range-picker"
            numberOfMonths={2}
            month={initialMonth}
            selectedDays={selectedDays}
            modifiers={modifiers}
            onDayClick={this.handleDayClick}
            onDayMouseEnter={this.mouseEnterDayHandler}
            todayButton="Today"
            disabledDays={disabledDays}
            fixedWeeks
          />

          {/* Time Picker */}
          {this.props.showTimePicker && (
            <BzmTimeRangePicker
              onTimeRangeChanged={this.timeRangeChanged}
              timeFrom={this.state.timeFrom}
              timeTo={this.state.timeTo}
              disableButton={!(from && to)}
              useRangeValidation={validateRange}
              exposeCurrentTime={this.getTimeRange}
            />
          )}
          {/* ./Time Picker */}
        </div>
      </div>
    );
  }
}

export default ReactDatePickerWrapper;
